/**
 * RuleCard - Reusable card for rule configuration
 * Supports toggle, inline inputs, and informational-only mode
 */

import React from 'react';

const Toggle = ({ enabled, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`
      w-10 h-6 rounded-full transition-colors relative
      ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      ${enabled ? 'bg-slate-900' : 'bg-slate-200'}
    `}
  >
    <div
      className={`
        absolute top-1 w-4 h-4 rounded-full bg-white transition-all
        ${enabled ? 'left-5' : 'left-1'}
      `}
    />
  </button>
);

const RuleCard = ({
  icon: Icon,
  iconColor = 'text-slate-400',
  iconBgColor = 'bg-slate-100',
  name,
  description,
  enabled,
  onToggle,
  isInfoOnly = false,
  children,
}) => {
  const isActive = isInfoOnly || enabled;

  return (
    <div
      className={`
        p-4 rounded-xl border-2 transition-colors
        ${isActive ? 'bg-slate-50 border-slate-200' : 'bg-slate-50/50 border-slate-100'}
      `}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          <div
            className={`
              w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
              ${isActive ? iconBgColor : 'bg-slate-100'}
            `}
          >
            <Icon
              className={`h-4 w-4 ${isActive ? iconColor : 'text-slate-400'}`}
            />
          </div>
          <div>
            <p
              className={`
                text-sm font-semibold
                ${isActive ? 'text-slate-900' : 'text-slate-400'}
              `}
            >
              {name}
            </p>
            {description && (
              <p className="text-xs text-slate-500 mt-0.5">{description}</p>
            )}
          </div>
        </div>

        {isInfoOnly ? (
          <span className="text-xs font-medium text-slate-500 bg-slate-200 px-2 py-1 rounded">
            Always On
          </span>
        ) : (
          <Toggle enabled={enabled} onChange={onToggle} />
        )}
      </div>

      {/* Config inputs - only show when enabled */}
      {isActive && children && (
        <div className="ml-11 mt-2">{children}</div>
      )}
    </div>
  );
};

export default RuleCard;
